/**
 * GridDashboard Component
 * The main customizable dashboard using react-grid-layout
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

// Create responsive grid with width provider for proper sizing
const ResponsiveGridLayout = WidthProvider(Responsive);

import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/dashboard/Header';
import { SettingsDialog } from '../components/dashboard/SettingsDialog';
import { WidgetCatalog, WidgetRenderer, OnboardingWizard, ShareDashboardDialog } from '../components/dashboard/widgets';
import { ExecutiveCardConfigDialog } from '../components/dashboard/widgets/ExecutiveCardWidget';
import { DashboardLinkConfigDialog } from '../components/dashboard/widgets/DashboardLinkWidget';
import { WIDGET_TYPES } from '../components/dashboard/widgets/widgetConfig';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { updateDashboardLayout, updateDashboard, getUserDashboards, createDashboard } from '../lib/api';

import {
  Plus,
  Edit3,
  Save,
  X,
  LayoutGrid,
  RefreshCw,
  Share2,
} from 'lucide-react';

export default function GridDashboard({ onShowAdmin, impersonatedEmail, viewDashboardId, isReadOnlyView }) {
  const { user, userDashboard, refreshAuth, isImpersonating, effectiveUser } = useAuth();
  
  // Use impersonated email if provided, otherwise use logged-in user's email
  const effectiveEmail = impersonatedEmail || user?.email;
  
  // Dashboard state - may be different from userDashboard when impersonating or viewing
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  
  // Container ref and width for responsive grid
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  // UI State
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [timeRange, setTimeRange] = useState('30 days');

  // Layout State
  const [layout, setLayout] = useState([]);
  const [originalLayout, setOriginalLayout] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Widget Configuration State
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configuringWidget, setConfiguringWidget] = useState(null);

  // Check if user should see onboarding (first time with empty dashboard)
  useEffect(() => {
    const dashboard = activeDashboard || userDashboard;
    if (dashboard && (!dashboard.layout || dashboard.layout.length === 0)) {
      // Check if user has dismissed onboarding before
      const dismissed = localStorage.getItem(`onboarding-dismissed-${effectiveEmail}`);
      if (!dismissed && !isImpersonating) {
        setShowOnboarding(true);
      }
    }
  }, [activeDashboard, userDashboard, effectiveEmail, isImpersonating]);

  // Fetch dashboard for impersonated user
  useEffect(() => {
    const fetchImpersonatedDashboard = async () => {
      if (!impersonatedEmail) {
        setActiveDashboard(null);
        return;
      }

      setLoadingDashboard(true);
      try {
        // Try to get existing dashboards for the impersonated user
        const response = await getUserDashboards(impersonatedEmail);
        const dashboards = response.data?.dashboards || response.data || [];
        
        console.log('[Impersonation] Fetched dashboards:', dashboards);
        
        if (dashboards.length > 0) {
          const dashboard = dashboards[0];
          // Ensure layout field exists
          if (!dashboard.layout) {
            dashboard.layout = [];
            console.log('[Impersonation] Dashboard had no layout field, initialized to empty array');
          }
          setActiveDashboard(dashboard);
          console.log('[Impersonation] Using dashboard:', dashboard.id, 'with', dashboard.layout.length, 'widgets');
        } else {
          // Create a blank dashboard for the user to customize
          console.log('[Impersonation] No dashboards found, creating blank one');
          
          const newDashResponse = await createDashboard({
            title: 'My Dashboard',
            description: 'Your personalized IT dashboard',
            dashboard_type: 'custom',
          }, impersonatedEmail);
          
          const newDashboard = newDashResponse.data;
          newDashboard.layout = newDashboard.layout || [];
          setActiveDashboard(newDashboard);
          toast.success(`Created blank dashboard for ${impersonatedEmail}`);
        }
      } catch (error) {
        console.error('[Impersonation] Failed to fetch dashboard:', error);
        toast.error('Failed to load user dashboard');
      } finally {
        setLoadingDashboard(false);
      }
    };

    fetchImpersonatedDashboard();
  }, [impersonatedEmail]);

  // Get the effective dashboard (viewed, impersonated, or own)
  const currentDashboard = viewDashboardId ? activeDashboard : (impersonatedEmail ? activeDashboard : userDashboard);

  // Fetch dashboard when viewing via link
  useEffect(() => {
    const fetchViewedDashboard = async () => {
      if (!viewDashboardId) return;
      
      setLoadingDashboard(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/dashboards/${viewDashboardId}`);
        if (response.ok) {
          const dashboard = await response.json();
          dashboard.layout = dashboard.layout || [];
          setActiveDashboard(dashboard);
        } else {
          toast.error('Dashboard not found');
        }
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
        toast.error('Failed to load dashboard');
      } finally {
        setLoadingDashboard(false);
      }
    };
    
    fetchViewedDashboard();
  }, [viewDashboardId]);

  // Measure container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    };

    // Initial measurement
    updateWidth();

    // Use ResizeObserver for responsive updates
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Fallback for window resize
    window.addEventListener('resize', updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // Initialize layout from dashboard
  useEffect(() => {
    if (currentDashboard) {
      const savedLayout = currentDashboard.layout || [];
      setLayout(savedLayout);
      setOriginalLayout(savedLayout);
    }
  }, [currentDashboard]);

  // Layout change handlers
  const handleLayoutChange = useCallback((newLayout, allLayouts) => {
    // Only process if we have widgets in layout state
    setLayout(prevLayout => {
      if (prevLayout.length === 0) return prevLayout;
      
      // Merge new positions with existing widget data (preserve config)
      const updatedLayout = prevLayout.map(widget => {
        const newPos = newLayout.find(l => l.i === widget.i);
        if (newPos) {
          return {
            ...widget,
            x: newPos.x,
            y: newPos.y,
            w: newPos.w,
            h: newPos.h,
          };
        }
        return widget;
      });
      
      // Check if positions actually changed before marking unsaved
      const hasPositionChanges = updatedLayout.some((widget, idx) => {
        const orig = prevLayout[idx];
        return orig && (widget.x !== orig.x || widget.y !== orig.y || widget.w !== orig.w || widget.h !== orig.h);
      });
      
      if (hasPositionChanges) {
        // Use a timeout to avoid state update during render
        setTimeout(() => setHasUnsavedChanges(true), 0);
      }
      
      return updatedLayout;
    });
  }, []);

  // Add widget to layout
  const handleAddWidget = useCallback((widgetInstance) => {
    setLayout(prevLayout => {
      // Find the lowest y position to place the new widget
      const maxY = prevLayout.reduce((max, w) => Math.max(max, w.y + w.h), 0);
      const newWidget = {
        ...widgetInstance,
        y: maxY,
        x: 0,
      };
      return [...prevLayout, newWidget];
    });
    setHasUnsavedChanges(true);
    setCatalogOpen(false);
    toast.success('Widget added! Click "Save Layout" to keep changes.');
  }, []);

  // Remove widget from layout
  const handleRemoveWidget = useCallback((widgetId) => {
    setLayout(prev => prev.filter(w => w.i !== widgetId));
    setHasUnsavedChanges(true);
    toast.success('Widget removed');
  }, []);

  // Update widget config (for editable widgets like text, metrics)
  const handleUpdateWidgetConfig = useCallback((widgetId, newConfig) => {
    setLayout(prev => prev.map(w => 
      w.i === widgetId ? { ...w, config: { ...w.config, ...newConfig } } : w
    ));
    setHasUnsavedChanges(true);
  }, []);

  // Open widget configuration dialog
  const handleConfigureWidget = useCallback((widgetId) => {
    const widget = layout.find(w => w.i === widgetId);
    if (widget) {
      setConfiguringWidget(widget);
      setConfigDialogOpen(true);
    }
  }, [layout]);

  // Save widget configuration
  const handleSaveWidgetConfig = useCallback((newConfig) => {
    if (configuringWidget) {
      handleUpdateWidgetConfig(configuringWidget.i, newConfig);
      toast.success('Widget configured! Click "Save Layout" to keep changes.');
    }
    setConfigDialogOpen(false);
    setConfiguringWidget(null);
  }, [configuringWidget, handleUpdateWidgetConfig]);

  // Enter edit mode
  const enterEditMode = () => {
    setOriginalLayout([...layout]);
    setIsEditMode(true);
    toast.info('Edit mode enabled. Drag to reposition, resize from corners.');
  };

  // Cancel edit mode
  const cancelEditMode = () => {
    setLayout(originalLayout);
    setIsEditMode(false);
    setHasUnsavedChanges(false);
    toast.info('Changes discarded');
  };

  // Save layout
  const saveLayout = async () => {
    if (!currentDashboard?.id) {
      toast.error('No dashboard found to save');
      console.error('[Save] No dashboard ID available');
      return;
    }

    if (!layout || layout.length === 0) {
      toast.warning('No widgets to save. Add some widgets first!');
      return;
    }

    console.log('[Save] Saving layout for dashboard:', currentDashboard.id);
    console.log('[Save] Layout to save:', layout);

    setIsSaving(true);
    try {
      const response = await updateDashboardLayout(currentDashboard.id, layout);
      console.log('[Save] Save response:', response);
      
      setOriginalLayout([...layout]);
      setIsEditMode(false);
      setHasUnsavedChanges(false);
      toast.success(`Dashboard saved! (${layout.length} widget${layout.length !== 1 ? 's' : ''})`);
      
      // Refresh auth to get updated dashboard (only for own dashboard)
      if (!impersonatedEmail) {
        await refreshAuth();
      } else {
        // Update local state for impersonated dashboard
        setActiveDashboard(prev => prev ? { ...prev, layout: [...layout] } : null);
      }
    } catch (error) {
      console.error('[Save] Failed to save layout:', error);
      toast.error(`Failed to save: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle onboarding completion
  const handleOnboardingComplete = async ({ name, template, layout: newLayout }) => {
    // Mark onboarding as dismissed
    localStorage.setItem(`onboarding-dismissed-${effectiveEmail}`, 'true');
    
    // Update dashboard name if provided
    if (name && currentDashboard?.id) {
      try {
        await updateDashboard(currentDashboard.id, { title: name });
      } catch (error) {
        console.error('Failed to update dashboard name:', error);
      }
    }

    // Set layout from template
    if (newLayout && newLayout.length > 0) {
      setLayout(newLayout);
      setHasUnsavedChanges(true);
      toast.success('Template applied! Click "Save Layout" to keep it.');
    } else {
      // Open catalog if blank template selected
      setCatalogOpen(true);
    }

    setShowOnboarding(false);
    if (!impersonatedEmail) {
      await refreshAuth();
    }
  };

  // Handle onboarding dismiss
  const handleOnboardingDismiss = () => {
    localStorage.setItem(`onboarding-dismissed-${effectiveEmail}`, 'true');
    setShowOnboarding(false);
  };

  // Generate grid layout items for react-grid-layout - memoized to prevent unnecessary recalculations
  const gridLayout = useMemo(() => layout.map(widget => ({
    i: widget.i,
    x: widget.x,
    y: widget.y,
    w: widget.w,
    h: widget.h,
    minW: widget.minW || 2,
    minH: widget.minH || 2,
    maxW: widget.maxW || 12,
    maxH: widget.maxH || 10,
  })), [layout]);

  // Dashboard title - show impersonated user's name if impersonating
  const dashboardTitle = impersonatedEmail 
    ? `${activeDashboard?.title || 'Dashboard'} (${impersonatedEmail})`
    : (currentDashboard?.title || `${user?.name?.split(' ')[0] || 'My'}'s Dashboard`);

  // Loading state for impersonated dashboard
  if (loadingDashboard) {
    return (
      <div className="dashboard-container bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading dashboard for {impersonatedEmail}...</p>
        </div>
      </div>
    );
  }

  // Empty dashboard state - show when layout is empty and catalog is closed
  if (layout.length === 0 && !catalogOpen && !hasUnsavedChanges) {
    return (
      <div className="dashboard-container bg-background min-h-screen" data-testid="grid-dashboard">
        <Header
          tenantName={dashboardTitle}
          onShowAdmin={onShowAdmin}
          onOpenSettings={() => setSettingsOpen(true)}
        />

        {/* Static link to admin dashboard for cvictoriano */}
        {effectiveEmail?.toLowerCase() === 'cvictoriano@therapymgmt.com' && !isReadOnlyView && (
          <div className="bg-primary/10 border-b border-primary/20 px-6 py-2">
            <div className="max-w-screen-2xl mx-auto flex items-center justify-center gap-2">
              <span className="text-sm text-muted-foreground">Quick Access:</span>
              <a
                href="/it-dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary hover:text-primary/80 underline underline-offset-2"
                data-testid="admin-dashboard-link"
              >
                Timothy Plunkett&apos;s Dashboard →
              </a>
            </div>
          </div>
        )}

        <main className="max-w-4xl mx-auto px-6 py-16">
          <div className="text-center">
            <div className="inline-flex p-4 rounded-2xl bg-primary/10 mb-6">
              <LayoutGrid className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Welcome, {effectiveUser?.name?.split(' ')[0] || 'there'}!
            </h1>
            <p className="text-lg text-muted-foreground max-w-md mx-auto mb-8">
              Your dashboard is empty. Start adding widgets to build your personalized view.
            </p>
            <Button
              size="lg"
              onClick={() => setCatalogOpen(true)}
              className="gap-2"
              data-testid="add-first-widget-btn"
            >
              <Plus className="w-5 h-5" />
              Add Your First Widget
            </Button>
          </div>
        </main>

        <WidgetCatalog
          open={catalogOpen}
          onOpenChange={setCatalogOpen}
          onAddWidget={handleAddWidget}
          existingWidgets={layout}
        />
        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        <OnboardingWizard
          open={showOnboarding}
          onOpenChange={handleOnboardingDismiss}
          onComplete={handleOnboardingComplete}
          userName={user?.name?.split(' ')[0] || 'there'}
        />
      </div>
    );
  }

  return (
    <div className="dashboard-container bg-background min-h-screen" data-testid="grid-dashboard">
      <Header
        tenantName={dashboardTitle}
        onShowAdmin={onShowAdmin}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      {/* Static link to admin dashboard for cvictoriano */}
      {effectiveEmail?.toLowerCase() === 'cvictoriano@therapymgmt.com' && !isReadOnlyView && (
        <div className="bg-primary/10 border-b border-primary/20 px-6 py-2">
          <div className="max-w-screen-2xl mx-auto flex items-center justify-center gap-2">
            <span className="text-sm text-muted-foreground">Quick Access:</span>
            <a
              href="/it-dashboard"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary hover:text-primary/80 underline underline-offset-2"
              data-testid="admin-dashboard-link"
            >
              Timothy Plunkett&apos;s Dashboard →
            </a>
          </div>
        </div>
      )}

      {/* Edit Mode / Unsaved Changes Toolbar */}
      <div className={cn(
        "sticky top-16 z-30 bg-card/95 backdrop-blur-sm border-b border-border/50 px-6 py-3 transition-all duration-300",
        (isEditMode || hasUnsavedChanges) ? "block" : "hidden"
      )}>
        <div className="flex items-center justify-between max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Edit3 className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {isEditMode ? 'Edit Mode' : 'Unsaved Changes'}
              </p>
              <p className="text-xs text-muted-foreground">
                {isEditMode ? 'Drag widgets to reposition. Resize from corners.' : 'Save your layout to keep changes.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCatalogOpen(true)}
              className="gap-2"
              data-testid="add-widget-btn"
            >
              <Plus className="w-4 h-4" />
              Add Widget
            </Button>
            {isEditMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelEditMode}
                className="gap-2"
                data-testid="cancel-edit-btn"
              >
                <X className="w-4 h-4" />
                Cancel
              </Button>
            )}
            <Button
              size="sm"
              onClick={saveLayout}
              disabled={isSaving}
              className="gap-2"
              data-testid="save-layout-btn"
            >
              {isSaving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Layout
            </Button>
          </div>
        </div>
      </div>

      {/* Floating Edit Button (when not in edit mode and no unsaved changes) */}
      {!isEditMode && !hasUnsavedChanges && layout.length > 0 && !isReadOnlyView && (
        <div className="fixed bottom-6 right-6 z-50 flex gap-2">
          <Button
            variant="outline"
            size="lg"
            onClick={() => setShareOpen(true)}
            className="gap-2 shadow-lg"
            data-testid="share-dashboard-btn"
          >
            <Share2 className="w-5 h-5" />
            Share
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => setCatalogOpen(true)}
            className="gap-2 shadow-lg"
            data-testid="add-more-widgets-btn"
          >
            <Plus className="w-5 h-5" />
            Add Widget
          </Button>
          <Button
            size="lg"
            onClick={enterEditMode}
            className="gap-2 shadow-lg"
            data-testid="edit-dashboard-btn"
          >
            <Edit3 className="w-5 h-5" />
            Edit Layout
          </Button>
        </div>
      )}

      {/* Main Dashboard Grid */}
      <main className="p-6 lg:p-8" ref={containerRef}>
        {layout.length > 0 ? (
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: gridLayout, md: gridLayout, sm: gridLayout, xs: gridLayout, xxs: gridLayout }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={80}
            onLayoutChange={handleLayoutChange}
            isDraggable={isEditMode}
            isResizable={isEditMode}
            draggableHandle=".drag-handle"
            margin={[16, 16]}
            containerPadding={[0, 0]}
            useCSSTransforms={true}
            measureBeforeMount={false}
          >
            {layout.map((widget) => (
              <div key={widget.i} data-testid={`grid-item-${widget.i}`}>
                <WidgetRenderer
                  widget={widget}
                  isEditMode={isEditMode}
                  onRemove={handleRemoveWidget}
                  onConfigure={handleConfigureWidget}
                  onUpdateWidgetConfig={handleUpdateWidgetConfig}
                  timeRange={timeRange}
                />
              </div>
            ))}
          </ResponsiveGridLayout>
        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No widgets yet. Add some from the catalog!</p>
          </div>
        )}
      </main>

      {/* Dialogs */}
      <WidgetCatalog
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        onAddWidget={handleAddWidget}
        existingWidgets={layout}
      />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <ShareDashboardDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        dashboard={currentDashboard}
      />
      <OnboardingWizard
        open={showOnboarding}
        onOpenChange={handleOnboardingDismiss}
        onComplete={handleOnboardingComplete}
        userName={impersonatedEmail ? impersonatedEmail.split('@')[0] : (user?.name?.split(' ')[0] || 'there')}
      />
      
      {/* Widget Configuration Dialogs */}
      {configuringWidget?.type === WIDGET_TYPES.EXECUTIVE_CARD_WIDGET && (
        <ExecutiveCardConfigDialog
          open={configDialogOpen}
          onOpenChange={(open) => {
            setConfigDialogOpen(open);
            if (!open) setConfiguringWidget(null);
          }}
          config={configuringWidget?.config}
          onSave={handleSaveWidgetConfig}
        />
      )}
      {configuringWidget?.type === WIDGET_TYPES.DASHBOARD_LINK_WIDGET && (
        <DashboardLinkConfigDialog
          open={configDialogOpen}
          onOpenChange={(open) => {
            setConfigDialogOpen(open);
            if (!open) setConfiguringWidget(null);
          }}
          config={configuringWidget?.config}
          onSave={handleSaveWidgetConfig}
        />
      )}
    </div>
  );
}
